import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService, DashboardStats } from '../services/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { useNotifications } from '../contexts/NotificationsContext';
import { useAuth } from '../contexts/AuthContext';
import {
  DocumentIcon,
  FolderIcon,
  BellIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ShieldCheckIcon,
  LanguageIcon,
  PaperAirplaneIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import { getTranslationStats } from '../utils/translationStats';

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const navigate = useNavigate();
  const { unreadCount } = useNotifications();
  const { user } = useAuth();
  const { t } = useTranslation('dashboard');
  
  // Set page title automatically based on route
  usePageTitle();
  
  // Determine if user is sysadmin or admin
  const isSysadmin = user?.role === 'sysadmin';
  const isAdmin = user?.role === 'admin';
  const isTranslator = !!(user?.role && user.role.startsWith('translator_'));
  
  // Track view mode to determine what data to fetch
  const [viewMode, setViewMode] = useState<'user' | 'admin' | 'sysadmin'>(() => {
    try {
      const saved = localStorage.getItem('view_mode');
      if (saved) {
        const mode = saved as 'user' | 'admin' | 'sysadmin';
        if (user?.role === 'sysadmin') {
          return mode;
        } else if (user?.role === 'admin') {
          return mode === 'sysadmin' ? 'admin' : mode;
        }
        return 'user';
      }
      if (user?.role === 'admin' || user?.role === 'sysadmin') {
        return 'admin';
      }
      return 'user';
    } catch {
      return 'user';
    }
  });
  
  const queryClient = useQueryClient();
  
  // Calculate effective mode for data fetching
  // For sysadmins: use viewMode (admin = company stats, sysadmin = system-wide)
  // For company admins: always admin mode (company stats)
  const effectiveMode = useMemo(() => {
    return isSysadmin ? viewMode : (isAdmin ? 'admin' : 'user');
  }, [isSysadmin, isAdmin, viewMode]);
  
  // Listen for view mode changes
  useEffect(() => {
    const handler = (event: CustomEvent) => {
      if (event.type !== 'view-mode-changed') {
        return;
      }
      const newMode = event.detail?.viewMode;
      if (newMode && newMode !== viewMode) {
        setViewMode(newMode as 'user' | 'admin' | 'sysadmin');
      }
    };
    window.addEventListener('view-mode-changed', handler as EventListener);
    return () => {
      window.removeEventListener('view-mode-changed', handler as EventListener);
    };
  }, [viewMode]);
  
  // Invalidate queries when effectiveMode changes
  useEffect(() => {
    // Invalidate all dashboard-related queries to refetch with new mode
    queryClient.invalidateQueries({ queryKey: ['companies-count'] });
    queryClient.invalidateQueries({ queryKey: ['folders-breakdown'] });
    queryClient.invalidateQueries({ queryKey: ['devices-count'] });
    queryClient.invalidateQueries({ queryKey: ['admin-folders-count'] });
    queryClient.invalidateQueries({ queryKey: ['users-count'] });
    queryClient.invalidateQueries({ queryKey: ['policies-count'] });
    queryClient.invalidateQueries({ queryKey: ['assets-count'] });
    queryClient.invalidateQueries({ queryKey: ['assets-shared-by-me-count'] });
    queryClient.invalidateQueries({ queryKey: ['assets-shared-with-me-count'] });
    queryClient.invalidateQueries({ queryKey: ['access-requests'] });
  }, [effectiveMode, queryClient]);

  const { data: dashboardData, isLoading, error } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => apiService.getDashboardStats(),
    gcTime: 0, // Don't cache - stop when component unmounts
  });


  // Fetch companies count (in admin and sysadmin modes)
  // For sysadmins in admin mode: filter to managed companies on frontend
  // For sysadmins in sysadmin mode: show all companies
  // For admins: backend already filters to managed companies
  const { data: companiesData } = useQuery({
    queryKey: ['companies-count', user?.company_id, effectiveMode],
    queryFn: async () => {
      if (effectiveMode === 'sysadmin' && isSysadmin) {
        // System-wide: get all companies
        return await apiService.getCompaniesCount();
      } else if (effectiveMode === 'admin' && user?.company_id) {
        // Admin mode: for sysadmins, get all companies and filter to managed ones on frontend
        // For regular admins, backend already filters to managed companies
        if (isSysadmin) {
          // For sysadmins in admin mode, filter on frontend
          const allCompanies = await apiService.getCompanies();
          // Filter to companies where managed_by = user's company_id, plus their own company
          const managedCompanies = allCompanies.filter((c: any) => 
            (c.managed_by === user.company_id) || (c.id === user.company_id)
          );
          const active = managedCompanies.filter((c: any) => c.is_active).length;
          const inactive = managedCompanies.length - active;
          return {
            total: managedCompanies.length,
            active,
            inactive,
          };
        } else {
          // For regular admins, backend already filters
          return await apiService.getCompaniesCount();
        }
      }
      return { total: 0, active: 0, inactive: 0 };
    },
    enabled: (effectiveMode === 'admin' || effectiveMode === 'sysadmin') && !!user,
    gcTime: 0,
  });

  // Fetch translation stats (for sysadmin and translators)
  const { data: translationStats } = useQuery({
    queryKey: ['translation-stats'],
    queryFn: getTranslationStats,
    enabled: isSysadmin || isTranslator,
    gcTime: 0,
  });

  // Fetch folders breakdown (only in sysadmin mode - system-wide)
  const { data: foldersData } = useQuery({
    queryKey: ['folders-breakdown', effectiveMode],
    queryFn: async () => {
      const folders = await apiService.getAutoEncryptFolders();
      const systemFolders = folders.filter(f => f.type === 'system' && f.is_template).length;
      const companyTemplates = folders.filter(f => f.type === 'company' && f.is_template).length;
      return {
        total: folders.length,
        system: systemFolders,
        companyTemplates,
      };
    },
    enabled: effectiveMode === 'sysadmin' && isSysadmin,
    gcTime: 0,
  });

  // Fetch devices count (based on effective mode)
  const { data: devicesData, error: devicesError } = useQuery({
    queryKey: ['devices-count', user?.company_id, effectiveMode],
    queryFn: async () => {
      if (effectiveMode === 'sysadmin' && isSysadmin) {
        // System-wide count for sysadmins in sysadmin mode
        const response = await apiService.get('/devices/count');
        return response.data.count || 0;
      } else if (effectiveMode === 'admin' && user?.company_id) {
        // Company-specific count for admins or sysadmins in admin mode
        console.log('🔍 DASHBOARD: Fetching company devices for company_id:', user.company_id, 'effectiveMode:', effectiveMode, 'isSysadmin:', isSysadmin);
        try {
          const response = await apiService.get(`/devices/company/${user.company_id}/count`);
          console.log('✅ DASHBOARD: Company device count:', response.data.count);
          return response.data.count || 0;
        } catch (err: any) {
          console.error('❌ DASHBOARD: Failed to get company device count:', err?.response?.data || err?.message || err);
          // If endpoint fails, try to get devices list and count on frontend
          try {
            console.log('🔄 DASHBOARD: Trying fallback - getting devices list...');
            const devicesResponse = await apiService.get(`/devices/company/${user.company_id}`);
            const devices = devicesResponse.data || [];
            console.log('✅ DASHBOARD: Fallback successful, device count:', devices.length);
            return devices.length;
          } catch (fallbackErr: any) {
            console.error('❌ DASHBOARD: Fallback also failed:', fallbackErr?.response?.data || fallbackErr?.message || fallbackErr);
            return 0;
          }
        }
      }
      return 0;
    },
    enabled: (effectiveMode === 'admin' || effectiveMode === 'sysadmin') && !!user,
    gcTime: 0,
  });

  // Fetch folders count (for admin mode - company-specific)
  const { data: adminFoldersData } = useQuery({
    queryKey: ['admin-folders-count', user?.company_id, effectiveMode],
    queryFn: async () => {
      const folders = await apiService.getAutoEncryptFolders();
      // Filter by company for admin mode
      const companyFolders = folders.filter(f => f.company_id === user?.company_id);
      return companyFolders.length;
    },
    enabled: effectiveMode === 'admin' && !!user?.company_id,
    gcTime: 0,
  });

  // Fetch users count (based on effective mode)
  const { data: usersData } = useQuery({
    queryKey: ['users-count', user?.company_id, effectiveMode],
    queryFn: async () => {
      if (effectiveMode === 'sysadmin' && isSysadmin) {
        // System-wide: get all users
        const response = await apiService.getUsers();
        return response.total;
      } else if (effectiveMode === 'admin' && user?.company_id) {
        // Admin mode: for sysadmins, get all users and filter on frontend
        // For regular admins, backend already filters to managed companies
        if (isSysadmin) {
          // For sysadmins in admin mode, get all users and filter to company
          const response = await apiService.getUsers();
          // Filter to users from the sysadmin's company
          const companyUsers = response.users?.filter((u: any) => u.company_id === user.company_id) || [];
          return companyUsers.length;
        } else {
          // For regular admins, backend already filters to managed companies
          const response = await apiService.getUsers({ company_id: user.company_id });
          return response.total;
        }
      }
      return 0;
    },
    enabled: (effectiveMode === 'admin' || effectiveMode === 'sysadmin') && !!user,
    gcTime: 0,
  });

  // Fetch policies count (based on effective mode)
  // Note: getPoliciesByRole uses GetAccessiblePolicies which already filters by user role/company
  // For sysadmins: returns all policies (system + all companies)
  // For company admins: returns company policies only
  // So we need to check if we can get company-specific policies for sysadmins in admin mode
  const { data: policiesData } = useQuery({
    queryKey: ['policies-count', user?.company_id, effectiveMode],
    queryFn: async () => {
      // In sysadmin mode: get all policies (system-wide)
      // In admin mode: get company-specific policies
      if (effectiveMode === 'sysadmin' && isSysadmin) {
        // Get all policies (system-wide)
        const policies = await apiService.getPoliciesByRole();
        return policies.length;
      } else if (effectiveMode === 'admin' && user?.company_id) {
        // Get company-specific policies using the type endpoint
        try {
          // Get company policies
          const companyResponse = await apiService.get(`/policies/type?type=company&company_id=${user.company_id}`);
          const companyPolicies = companyResponse.data.policies || [];
          
          // Also get user policies from this company
          const userResponse = await apiService.get(`/policies/type?type=user&company_id=${user.company_id}`);
          const userPolicies = userResponse.data.policies || [];
          
          // Get system policies (accessible to all)
          const systemResponse = await apiService.get('/policies/type?type=system');
          const systemPolicies = systemResponse.data.policies || [];
          
          // Combine and deduplicate
          const allPolicies = [...systemPolicies, ...companyPolicies, ...userPolicies];
          const uniquePolicies = Array.from(new Map(allPolicies.map(p => [p.id, p])).values());
          return uniquePolicies.length;
        } catch (err) {
          // Fallback: if type endpoint fails, try to get company-specific policies
          // For sysadmins in admin mode, we can't use getPoliciesByRole as it returns all policies
          console.error('Failed to get company-specific policies:', err);
          // Try to get policies using the type endpoint with just company_id
          try {
            const companyResponse = await apiService.get(`/policies/type?type=company&company_id=${user.company_id}`);
            return (companyResponse.data.policies || []).length;
          } catch (fallbackErr) {
            console.error('Fallback also failed:', fallbackErr);
            return 0; // Return 0 rather than system-wide count
          }
        }
      }
      return 0;
    },
    enabled: (effectiveMode === 'admin' || effectiveMode === 'sysadmin') && !!user,
    gcTime: 0,
  });

  // Fetch assets count (based on effective mode)
  const { data: assetsData } = useQuery({
    queryKey: ['assets-count', user?.company_id, effectiveMode],
    queryFn: async () => {
      // In sysadmin mode: get all files (system-wide)
      // In admin mode: get company-specific files (for both admins and sysadmins in admin mode)
      if (effectiveMode === 'sysadmin' && isSysadmin) {
        // System-wide count for sysadmins in sysadmin mode
        const response = await apiService.get('/files/count');
        return response.data.count || 0;
      } else if (effectiveMode === 'admin' && user?.company_id) {
        // Company-specific count for admins or sysadmins in admin mode
        try {
          const response = await apiService.get(`/files/count?company_id=${user.company_id}`);
          return response.data.count || 0;
        } catch (err: any) {
          console.error('Failed to get company file count:', err);
          // Fallback: for sysadmins, backend might not support company_id param yet
          // In that case, return 0 or try a different approach
          return 0;
        }
      }
      return 0;
    },
    enabled: (effectiveMode === 'admin' || effectiveMode === 'sysadmin') && !!user,
    gcTime: 0,
  });

  // Assets shared by me (user mode) - count of my assets that have at least one recipient
  const { data: assetsSharedByMeCount } = useQuery({
    queryKey: ['assets-shared-by-me-count'],
    queryFn: () => apiService.getFilesSharedByMeCount(),
    enabled: effectiveMode === 'user' && !!user,
    gcTime: 0,
  });

  // Assets shared with me (user mode) - count of assets where I am a recipient
  const { data: assetsSharedWithMeCount } = useQuery({
    queryKey: ['assets-shared-with-me-count'],
    queryFn: () => apiService.getSharedWithMeCount(),
    enabled: effectiveMode === 'user' && !!user,
    gcTime: 0,
  });

  // Access requests: received (incoming) and my-requests (outgoing) for pending counts
  const { data: receivedRequestsData } = useQuery({
    queryKey: ['access-requests', 'received'],
    queryFn: () => apiService.getAllReceivedAccessRequests(),
    enabled: effectiveMode === 'user' && !!user,
    gcTime: 0,
  });
  const { data: myRequestsData } = useQuery({
    queryKey: ['access-requests', 'my-requests'],
    queryFn: () => apiService.getMyAccessRequests(),
    enabled: effectiveMode === 'user' && !!user,
    gcTime: 0,
  });
  const pendingIncoming = receivedRequestsData?.requests?.filter((r: { status: string }) => r.status === 'pending').length ?? 0;
  const pendingOutgoing = myRequestsData?.requests?.filter((r: { status: string }) => r.status === 'pending').length ?? 0;

  useEffect(() => {
    if (dashboardData) {
      setStats(dashboardData);
    }
  }, [dashboardData]);



  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('errors.loadingError')}</h3>
        <p className="mt-1 text-sm text-gray-500">{t('errors.tryAgain')}</p>
      </div>
    );
  }

  // Render stat card component
  const StatCard = ({ 
    title, 
    value, 
    subtitle,
    icon: Icon, 
    iconBgColor, 
    iconColor, 
    onClick, 
    isLoading: cardLoading 
  }: {
    title: string;
    value: number | string;
    subtitle?: string;
    icon: React.ComponentType<{ className?: string }>;
    iconBgColor: string;
    iconColor: string;
    onClick: () => void;
    isLoading?: boolean;
  }) => (
    <div 
      className="card cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
    >
      {cardLoading ? (
        <div className="animate-pulse">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
              <div>
                <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-12"></div>
              </div>
            </div>
            <div className="w-5 h-5 bg-gray-200 rounded"></div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`w-12 h-12 ${iconBgColor} rounded-lg flex items-center justify-center`}>
              <Icon className={`w-6 h-6 ${iconColor}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">{title}</p>
              <p className="text-3xl font-bold text-gray-900">{value}</p>
              {subtitle && (
                <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
              )}
            </div>
          </div>
          <ArrowRightIcon className="w-5 h-5 text-gray-400" />
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Key Metrics - Role-based */}
      {isTranslator ? (
        // Translators see only the translations card
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <StatCard
            title={t('stats.translations')}
            value={translationStats?.languages.filter(l => l.code !== 'en').length ?? 0}
            subtitle={t('stats.languages')}
            icon={LanguageIcon}
            iconBgColor="bg-purple-100"
            iconColor="text-purple-600"
            onClick={() => navigate('/translations')}
            isLoading={!translationStats}
          />
        </div>
      ) : effectiveMode === 'user' ? (
        // User/Standard mode dashboard (for all users including admins and sysadmins in user mode)
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Secured Assets */}
          <StatCard
            title={t('stats.securedAssets')}
            value={stats?.secured_assets || 0}
            icon={DocumentIcon}
            iconBgColor="bg-governate-100"
            iconColor="text-governate-600"
            onClick={() => navigate('/assets')}
            isLoading={isLoading}
          />

          {/* Monitored Folders */}
          <StatCard
            title={t('stats.monitoredFolders')}
            value={stats?.monitored_folders || 0}
            icon={FolderIcon}
            iconBgColor="bg-quantum-100"
            iconColor="text-quantum-600"
            onClick={() => navigate('/folders')}
            isLoading={isLoading}
          />

          {/* Assets shared by me - links to Assets (My assets) */}
          <StatCard
            title={t('stats.assetsSharedByMe')}
            value={assetsSharedByMeCount ?? 0}
            icon={ShareIcon}
            iconBgColor="bg-emerald-100"
            iconColor="text-emerald-600"
            onClick={() => navigate('/assets?tab=my-assets')}
            isLoading={assetsSharedByMeCount === undefined}
          />

          {/* Assets shared with me - links to Shared with me tab */}
          <StatCard
            title={t('stats.assetsSharedWithMe')}
            value={assetsSharedWithMeCount ?? 0}
            icon={DocumentIcon}
            iconBgColor="bg-sky-100 dark:bg-sky-900/30"
            iconColor="text-sky-600 dark:text-sky-400"
            onClick={() => navigate('/assets?tab=shared-with-me')}
            isLoading={assetsSharedWithMeCount === undefined}
          />

          {/* Pending access requests - inbound + outbound */}
          <div
            className="card cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/assets?tab=requests')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                  <PaperAirplaneIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('stats.pendingAccessRequests')}</p>
                  <div className="flex items-center gap-4 mt-1">
                    <span className="inline-flex items-center gap-1.5 text-gray-900 dark:text-white" title={t('stats.incoming')}>
                      <ArrowLeftIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-2xl font-bold">{pendingIncoming}</span>
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-gray-900 dark:text-white" title={t('stats.outgoing')}>
                      <PaperAirplaneIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-2xl font-bold">{pendingOutgoing}</span>
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('stats.incoming')} / {t('stats.outgoing')}</p>
                </div>
              </div>
              <ArrowRightIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
        </div>
      ) : (effectiveMode === 'admin' || effectiveMode === 'sysadmin') ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Organisations (in admin and sysadmin modes) */}
          <StatCard
            title={t('stats.organisations')}
            value={companiesData?.total ?? 0}
            subtitle={
              companiesData?.total === companiesData?.active
                ? t('stats.allActive')
                : `${companiesData?.active ?? 0} ${t('stats.active')}, ${companiesData?.inactive ?? 0} ${t('stats.inactive')}`
            }
            icon={BuildingOfficeIcon}
            iconBgColor="bg-blue-100"
            iconColor="text-blue-600"
            onClick={() => navigate('/clients')}
            isLoading={companiesData === undefined}
          />
          
          {/* Folders */}
          {(effectiveMode === 'admin' || effectiveMode === 'sysadmin') && (
            <StatCard
              title={t('stats.folders')}
              value={effectiveMode === 'sysadmin' ? (foldersData?.total ?? 0) : (user?.company_id ? (adminFoldersData ?? 0) : 0)}
              subtitle={
                effectiveMode === 'sysadmin' && foldersData && foldersData.total > 0
                  ? `${foldersData.system} system, ${foldersData.companyTemplates} company templates`
                  : undefined
              }
              icon={FolderIcon}
              iconBgColor="bg-quantum-100"
              iconColor="text-quantum-600"
              onClick={() => navigate('/folders')}
              isLoading={effectiveMode === 'sysadmin' ? foldersData === undefined : (user?.company_id ? adminFoldersData === undefined : false)}
            />
          )}
          
          {/* Translations (only in sysadmin mode) */}
          {effectiveMode === 'sysadmin' && isSysadmin && (
            <StatCard
              title={t('stats.translations')}
              value={translationStats?.languages.filter(l => l.code !== 'en').length ?? 0}
              subtitle={t('stats.languages')}
              icon={LanguageIcon}
              iconBgColor="bg-purple-100"
              iconColor="text-purple-600"
              onClick={() => navigate('/translations')}
              isLoading={!translationStats}
            />
          )}

          {/* Devices */}
          {(effectiveMode === 'admin' || effectiveMode === 'sysadmin') && (
            <StatCard
              title={t('stats.devices')}
              value={devicesData ?? 0}
              icon={DocumentIcon}
              iconBgColor="bg-indigo-100"
              iconColor="text-indigo-600"
              onClick={() => navigate('/devices')}
              isLoading={devicesData === undefined && !devicesError}
            />
          )}
          
          {/* Users */}
          {(effectiveMode === 'admin' || effectiveMode === 'sysadmin') && (
            <StatCard
              title={t('stats.users')}
              value={usersData ?? 0}
              icon={UsersIcon}
              iconBgColor="bg-purple-100"
              iconColor="text-purple-600"
              onClick={() => navigate('/users')}
              isLoading={usersData === undefined}
            />
          )}
          
          {/* Policies */}
          {(effectiveMode === 'admin' || effectiveMode === 'sysadmin') && (
            <StatCard
              title={t('stats.policies')}
              value={policiesData ?? 0}
              icon={ShieldCheckIcon}
              iconBgColor="bg-orange-100"
              iconColor="text-orange-600"
              onClick={() => navigate('/policies')}
              isLoading={policiesData === undefined}
            />
          )}
          
          {/* Assets */}
          {(effectiveMode === 'admin' || effectiveMode === 'sysadmin') && (
            <StatCard
              title={t('stats.assets')}
              value={assetsData ?? 0}
              icon={DocumentIcon}
              iconBgColor="bg-governate-100"
              iconColor="text-governate-600"
              onClick={() => navigate('/assets')}
              isLoading={assetsData === undefined}
            />
          )}
        </div>
      ) : null}


      {/* Notifications Section - Show for all users including translators */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t('notifications.title')}
            </h2>

          </div>
          <button 
            onClick={() => navigate('/notifications')}
            className="btn-secondary flex items-center space-x-2"
          >
            <BellIcon className="w-4 h-4" />
            <span>{t('notifications.viewAll')}</span>
          </button>
        </div>

        <div className="space-y-4">
          {unreadCount > 0 ? (
            <div 
              className="flex items-center justify-between p-4 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors"
              onClick={() => navigate('/notifications')}
            >
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <BellIcon className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {unreadCount === 1 ? t('notifications.newNotification') : t('notifications.newNotifications', { count: unreadCount })}
                  </p>
                  <p className="text-sm text-gray-600">
                    {unreadCount === 1 
                      ? t('notifications.unreadNotification') 
                      : t('notifications.unreadNotifications', { count: unreadCount })
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">{t('notifications.clickToView')}</span>
                <ArrowRightIcon className="w-4 h-4 text-gray-400" />
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">🔔</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('notifications.noNewNotifications')}</h3>
              <p className="text-gray-600">{t('notifications.allCaughtUp')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Service Status */}
    
    </div>
  );
};

export default Dashboard;
